import { Disclaimer, EmailDisclaimerText, Form as Form$1, MARKETING_SMS_CONSENT } from "./auth-fe15.esm.js";
import { NamelessAccountContext } from "./auth-fe18.esm.js";
import React, { forwardRef, useCallback, useContext, useEffect, useState } from "react";
import { AccountFormControls, AccountPasswordInput, Box, Button, Link, NameInput, PhoneInput, SimpleCheckbox, Text } from "@opendoor/bricks-next";
//#region src/AccountForm/AccountFormCinderblocks/AccountPasswordForm.tsx
var AccountPasswordForm = forwardRef(({ name: initialName, phone: initialPhone, onRegister, onLogin, hidePasswordField, isNewHuman, onboardingAccountPageCTA, passwordFormActionText, analyticsName, fullNameLabel, fullNamePlaceholder, loading, loginError, phoneNumberInputOptional = true, phoneNumberInputHidden = false, showPasswordlessLogin, onRequestPasswordlessLogin, isRequestingPasswordlessLogin, openForgotPasswordInNewTab, fullNameOptional, fullNameHidden }, ref) => {
	const context = useContext(NamelessAccountContext);
	const [hasError, setHasError] = useState(false);
	const [name, setName] = useState(initialName || "");
	const [hasNameError, setNameError] = useState(false);
	const [hasPasswordError, setHasPasswordError] = useState(false);
	const [phone, setPhone] = useState(initialPhone || "");
	const [smsSubscribed, setSmsSubscribed] = useState(false);
	const [hasPhoneError, setPhoneError] = useState(!phoneNumberInputOptional);
	const [password, setPassword] = useState("");
	const [lastSubmit, setLastSubmit] = useState({});
	const getPhoneState = useCallback((phone) => {
		if (phone == null || phone === "") return {
			hasError: !phoneNumberInputOptional,
			value: phone
		};
		return {
			hasError: false,
			value: phone
		};
	}, [phoneNumberInputOptional]);
	const maybePrefillAccount = useCallback(() => {
		const url = new URL(window.location.href);
		const params = new URLSearchParams(url.searchParams);
		const { value, hasError } = getPhoneState(params.get("phoneNumber") || initialPhone);
		if (params.get("name") || params.get("phoneNumber")) {
			setName(params.get("name") ?? "");
			setPhone(hasError ? "" : value ?? "");
			setPhoneError(hasError);
		}
	}, [getPhoneState, initialPhone]);
	useEffect(() => {
		maybePrefillAccount();
		if (context.onPasswordFormLoad) context.onPasswordFormLoad();
	}, [context, maybePrefillAccount]);
	const validatePasswordLength = (password) => password.length >= 5 && password.length <= 128;
	const onNameChange = (name) => {
		setName(name);
		const isNameEmpty = name == null || name === "";
		const isNameValid = name.trim() !== "";
		if (fullNameOptional) setNameError(isNameEmpty ? false : !isNameValid);
		else setNameError(isNameEmpty || !isNameValid);
	};
	const handlePhoneValidationChange = (validity) => {
		setPhoneError(validity !== "valid");
	};
	const onPasswordChange = (password) => {
		setHasPasswordError(false);
		setHasError(password == null || !validatePasswordLength(password));
		setPassword(password);
	};
	const handleSubmit = (e) => {
		e.preventDefault();
		if (hasError || isNewHuman && (hasNameError || hasPhoneError)) return;
		if (Object.values(lastSubmit).filter(Boolean).length !== 0 && name === lastSubmit.name && password === lastSubmit.password && phone === lastSubmit.phone) return;
		if (isNewHuman) if (context.shouldRegisterOnEmailSubmit && context.onAccountUpdate) context.onAccountUpdate(name, phone, smsSubscribed);
		else onRegister(password, name, phone);
		else onLogin(password);
		setLastSubmit({
			name,
			password,
			phone
		});
	};
	const shouldDisableSubmit = hasError || !hidePasswordField && (!password || password.length === 0) || isNewHuman && (!fullNameOptional && (!name || hasNameError) || hasPhoneError);
	let actionText = "Confirm";
	if (phoneNumberInputHidden) actionText = "Next";
	else if (onboardingAccountPageCTA) actionText = "Continue";
	if (passwordFormActionText) actionText = passwordFormActionText;
	const showPasswordError = hasPasswordError && isNewHuman || loginError, passwordErrorText = loginError ? loginError : "Your password does not meet the requirements";
	const shouldRenderDisclaimerAboveCTA = !phoneNumberInputHidden && isNewHuman;
	const shouldRenderDisclaimerBelowCTA = !shouldRenderDisclaimerAboveCTA;
	useEffect(() => {
		if (showPasswordError && passwordErrorText) {
			if (ref && "current" in ref && ref.current) ref.current.focus();
		}
	}, [showPasswordError, passwordErrorText]);
	return /* @__PURE__ */ React.createElement(Box, null, /* @__PURE__ */ React.createElement(Form$1, { onSubmitCapture: handleSubmit }, /* @__PURE__ */ React.createElement(Box, { marginBottom: "$20x" }, /* @__PURE__ */ React.createElement(Box, { gap: "$20x" }, isNewHuman && !fullNameHidden && /* @__PURE__ */ React.createElement(NameInput, {
		analyticsName,
		autoComplete: "name",
		autoFocus: true,
		errorText: hasNameError ? "Please enter your full name." : void 0,
		title: fullNameLabel ? fullNameLabel : "Full name",
		onChangeText: onNameChange,
		placeholder: fullNamePlaceholder ? fullNamePlaceholder : "",
		required: !fullNameOptional,
		prefilledValue: name,
		invalidNameError: "Please enter your full name.",
		size: "medium"
	}), !hidePasswordField && /* @__PURE__ */ React.createElement(AccountPasswordInput, {
		analyticsName,
		autoFocus: !isNewHuman,
		supportText: "Your password must be 6 to 128 characters long",
		errorText: showPasswordError && passwordErrorText,
		onBlur: () => {
			setHasPasswordError(password.length > 0 && !validatePasswordLength(password));
		},
		title: "Password",
		ref,
		onChangeText: onPasswordChange,
		prefilledValue: password,
		size: "medium"
	}), isNewHuman && !phoneNumberInputHidden && /* @__PURE__ */ React.createElement(Box, { gap: "$5x" }, /* @__PURE__ */ React.createElement(PhoneInput, {
		analyticsName,
		title: "Phone number",
		onChangeText: (value) => setPhone(value),
		onValidationChange: handlePhoneValidationChange,
		required: !phoneNumberInputOptional,
		prefilledValue: phone,
		invalidPhoneError: "Please enter a valid phone number",
		fieldRequiredError: "This field is required",
		size: "medium"
	}), analyticsName === "reception-fe-account-form" && /* @__PURE__ */ React.createElement(SimpleCheckbox, {
		size: "xsmall",
		label: MARKETING_SMS_CONSENT.checkboxLabel,
		value: "value",
		analyticsName: `${analyticsName}-sms-checkbox`,
		onPress: (_, checked) => {
			if (typeof checked === "boolean") setSmsSubscribed(checked);
		},
		name: "marketing-opt-in"
	}))), /* @__PURE__ */ React.createElement(Box, {
		mt: shouldRenderDisclaimerAboveCTA ? "$0x" : "$4x",
		$largerThanSM: { marginBottom: shouldRenderDisclaimerAboveCTA ? "$10x" : null },
		$smallerThanMD: { marginBottom: shouldRenderDisclaimerAboveCTA ? "$5x" : null }
	}, shouldRenderDisclaimerAboveCTA && /* @__PURE__ */ React.createElement(EmailDisclaimerText, { analyticsName })), /* @__PURE__ */ React.createElement(AccountFormControls, {
		actionText,
		analyticsName,
		loading,
		showForgotPassword: !isNewHuman,
		onRequestPasswordlessLogin,
		isRequestingPasswordlessLogin,
		disabled: shouldDisableSubmit,
		submitA11yProps: { "aria-label": "" },
		openForgotPasswordInNewTab,
		forgotPasswordText: "Forgot your password?",
		forgotPasswordAriaNewWindow: "Opens a new window with the forgot password page",
		forgotPasswordAriaSameWindow: "Opens the forgot password page"
	}))), showPasswordlessLogin && /* @__PURE__ */ React.createElement(Box, {
		"data-testid": "passwordless-login",
		paddingTop: "$20x",
		marginBottom: "$20x",
		borderTopWidth: 1,
		borderTopColor: "$borderPrimary"
	}, /* @__PURE__ */ React.createElement(Text, {
		typography: "$subheaderSmall",
		tag: "h3",
		paddingBottom: "$10x",
		textAlign: "left"
	}, "Tired of resetting your password?"), /* @__PURE__ */ React.createElement(Text, {
		typography: "$bodySmall",
		tag: "h3",
		textAlign: "left",
		paddingBottom: "$20x"
	}, "We can email you a link that will simply log you into your account."), /* @__PURE__ */ React.createElement(Button, {
		variant: "secondary",
		loading: isRequestingPasswordlessLogin,
		analyticsName: `${analyticsName}-email-login-link`,
		"aria-label": null,
		onPress: onRequestPasswordlessLogin,
		size: "medium",
		label: "Email login link"
	})), shouldRenderDisclaimerBelowCTA && /* @__PURE__ */ React.createElement(Disclaimer, { mt: 5 }, "Your information is protected by our\xA0", /* @__PURE__ */ React.createElement(Link, {
		href: "https://www.opendoor.com/w/privacy",
		target: "_blank",
		analyticsName: `${analyticsName}-privacy-policy`,
		"aria-label": "Opens a new page with Opendoor’s privacy policy",
		typography: "$bodyXsmall",
		onPress: () => {}
	}, "Privacy Policy"), ".", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("br", null)));
});
AccountPasswordForm.displayName = "AccountPasswordForm";
//#endregion
export { AccountPasswordForm as default };

